"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Crown, Shield, Sword } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function LAmoureuxPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-pink-500/10 border border-pink-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-pink-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-pink-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-pink-500">/</span>
            <span className="text-sm">Major Arkana</span>
            <span className="text-pink-500">/</span>
            <span className="text-sm">L'Amoureux</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/systems/marseille/major-arcana"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Major Arkana'ya Geri Dön
          </Link>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-pink-900/20 to-rose-900/20 rounded-xl p-8 mb-8 border border-pink-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/marseille-l-amoureux.svg"
                  alt="L'Amoureux Tarot Card"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    if (target.nextElementSibling) {
                      (target.nextElementSibling as HTMLElement).style.display = 'flex';
                    }
                  }}
                />
                <div className="hidden w-full h-full bg-gradient-to-b from-pink-900/40 to-rose-900/40 border border-pink-500/30 flex-col items-center justify-center text-pink-300">
                  <span className="text-3xl font-bold mb-2">VI</span>
                  <span className="text-lg font-semibold">L'AMOUREUX</span>
                  <span className="text-sm text-center mt-2">The Lovers</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">VI</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">L'Amoureux</h1>
                  <p className="text-xl text-pink-400">Aşıklar / Aşk ve Seçim</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Marseille geleneğinde altıncı numara. Aşk, ilişki ve seçim kararıyla karşı karşıya kalma
                durumunu temsil eden Amoureux, duygusal bağlılık ve partnerliği simgeler.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-pink-500/20 text-pink-300 rounded-full text-sm">Aşk</span>
                <span className="px-3 py-1 bg-pink-500/20 text-pink-300 rounded-full text-sm">Seçim</span>
                <span className="px-3 py-1 bg-pink-500/20 text-pink-300 rounded-full text-sm">İlişki</span>
                <span className="px-3 py-1 bg-pink-500/20 text-pink-300 rounded-full text-sm">Partnerlik</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Heart className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Gerçek Aşk</h3>
                  <p className="text-slate-400 text-sm">Derin duygusal bağlılık, karşılıklı sevgi ve ruhsal birleşme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Uyumlu Partnerlik</h3>
                  <p className="text-slate-400 text-sm">İki ruhun birleşmesi, denge ve karşılıklı anlayış.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Doğru Seçim</h3>
                  <p className="text-slate-400 text-sm">Kalp ve akıl arasındaki dengeyi bulma, bilinçli karar verme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Ruhsal Birlik</h3>
                  <p className="text-slate-400 text-sm">Manevi bağlılık, ikiz ruh bulma ve ruhsal evlilik.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sword className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">İlişki Sorunları</h3>
                  <p className="text-slate-400 text-sm">Uyumsuzluk, ayrışmalar ve duygusal uzaklık.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yanlış Seçimler</h3>
                  <p className="text-slate-400 text-sm">Acele kararlar, hata yapma ve pişmanlık yaşama.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kararsızlık</h3>
                  <p className="text-slate-400 text-sm">İki seçim arasında kalma, karar verememe ve bocalam.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sadakatsizlik</h3>
                  <p className="text-slate-400 text-sm">Aldatma, güven kaybı ve bağlılık sorunları.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille Sembolizmi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-pink-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Heart className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Aşıklar</h3>
              <p className="text-slate-400 text-sm">İki insan arasındaki aşk ve bağlılık.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">☀️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Güneş</h3>
              <p className="text-slate-400 text-sm">İlahi ışık, bereket ve pozitif enerji.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👼</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Melek</h3>
              <p className="text-slate-400 text-sm">İlahi rehberlik ve manevi koruma.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">💘</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Cupid'in Oku</h3>
              <p className="text-slate-400 text-sm">Aşkın gücü, tutku ve çekim.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🌳</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Ağaçlar</h3>
              <p className="text-slate-400 text-sm">Yaşam ağacı ve bilgi ağacı, seçim ve karar.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🛤️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Yol Ayrımı</h3>
              <p className="text-slate-400 text-sm">Seçim yapma, karar verme ve yeni yönelim.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Yeni aşk ilişkisi</li>
                <li>• Evlilik teklifi</li>
                <li>• Ruhsal eş bulma</li>
                <li>• Duygusal birleşme</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İş ortaklığı</li>
                <li>• Takım çalışması</li>
                <li>• Yaratıcı işbirliği</li>
                <li>• Müşteri ilişkileri</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Crown className="h-5 w-5 text-purple-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Kendini tanıma</li>
                <li>• Denge bulma</li>
                <li>• Seçim yapma yetisi</li>
                <li>• Duygusal olgunluk</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-400" />
                Manevi Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Ruhsal bağlılık</li>
                <li>• Kader ortağı</li>
                <li>• Manevi birleşme</li>
                <li>• İlahi aşk</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/major-arcana/le-pape"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: Le Pape
          </Link>

          <Link
            href="/about/tarot/systems/marseille/major-arcana/le-chariot"
            className="inline-flex items-center px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded-lg transition-colors"
          >
            Sonraki: Le Chariot
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}